import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { Platform, ModalController, AlertController, LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { APP_CONFIG, AppConfig } from './app.config';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Keyboard, KeyboardResize, KeyboardInfo } from '@capacitor/keyboard';
import { Constants } from '../models/contants.models';
import { MyEvent } from 'src/services/myevent.services';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { GlobalProvider } from '../providers/global';
import { Router } from '@angular/router';
import { Storage } from '@ionic/storage-angular';
import { Socket } from 'ngx-socket-io';
import { filter, firstValueFrom, lastValueFrom } from 'rxjs';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';
import { IntentHandlerService } from './services/intent-handler.service';
import { SharedFileService } from './services/shared-file.service';
import StartupSync from './services/startup-sync.plugin';
// Removed LoadingControllerOverrideService import
import '../types/onesignal.d.ts';

interface UserInfo {
  id: string;
  password: string;
  deviceID: string;
  devtoken: string;
  pushdev: boolean;
}

interface ChatUser {
  id: string;
  password: string;
  name: string;
  role: string;
}

@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  rtlSide = "left";
  selectedIndex: any;
  appPages: any;
  
  // User properties
  id: string = '';
  password: string = '';
  name: string = '';
  chatuser: ChatUser | null = null;
  
  // Push notification properties
  pushid: string = '';
  pushtoken: string = '';
  pushdev: boolean = false;
  
  // UI properties
  protected app_version: string = '';
  
  // OneSignal properties
  uid: any;
  utoken: any;

  // Subscriptions
  private subscriptions: Subscription[] = [];
  private foregroundGuardAttached = false;
  private startupReleased = false;
  private oneSignalWebInitPromise: Promise<OneSignalWebSdk> | null = null;
  private emojiPreloadStarted = false;
  private readonly emojiFallbackVersion = '20260802-v2';

  // Constants
  private readonly ONESIGNAL_APP_ID = '67faeb85-f54e-4247-9994-1eb639893de0';
  private readonly API_BASE_URL = 'https://app.ivydental.com.tw/mobile/php/';
  private readonly CHAT_API_BASE_URL = 'https://chat.ivydental.com.tw/php/';

  constructor(
    @Inject(APP_CONFIG) public config: AppConfig, 
    private platform: Platform, 
    private modalController: ModalController,
    private translate: TranslateService, 
    private myEvent: MyEvent,
    private alertCtrl: AlertController,
    public storage: Storage,
    private http: HttpClient,
    private loadingCtrl: LoadingController,
    public global: GlobalProvider,
    private router: Router,
    private socket: Socket,
    private intentHandler: IntentHandlerService,
    private sharedFileService: SharedFileService,
    // Removed LoadingControllerOverrideService injection
  ) {
    this.initializeApp();
    this.setupGlobalAriaHiddenObserver();
  }
  

  async ngOnInit() {
    await this.storage.create();
    
    // Subscribe to platform resume event
    const resumeSub = this.platform.resume.subscribe(() => {
      this.clearAllNotifications();
    });
    this.subscriptions.push(resumeSub);

    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.clearAllNotifications();
      }
    });

    // Listen for shared data
    window.addEventListener('sharedDataReceived', (event: any) => {
      this.handleSharedData(event.detail.data);
    });
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private async initializeApp() {
    // Ensure Storage is initialized before any read, especially on Edge/Web.
    await this.storage.create();
    // [INFO] Removed loading spinner for cleaner startup
    await this.checkStorageData();
    if (this.chatuser) {
      await this.verifyAndNavigate();
    } else {
      await this.router.navigate(['/sign-in'], { replaceUrl: true });
    }

    await this.platform.ready();
    this.clearAllNotifications();
    if (this.isNativeRuntime()) {
      await this.setupStatusBar();
      await this.setupKeyboardBehavior();
    }

    if (this.shouldSetupOneSignal() && (window as any)?.plugins?.OneSignal) {
      await this.setupOneSignal();
    } else if (!this.isNativeRuntime()) {
      await this.setupOneSignalWeb();
    }

    if (this.shouldCheckVersion()) {
      await this.checkVersion();
    }

    await this.releaseStartupSurface();
  }



  private async releaseStartupSurface() {
    if (!this.isNativeRuntime() || this.startupReleased) {
      return;
    }

    try {
      await this.waitForFirstRoutePaint();
      await StartupSync.markReady();
    } catch (error) {
      console.warn('StartupSync markReady failed', error);
    }

    this.startupReleased = true;
  }

  private async waitForFirstRoutePaint() {
    const currentUrl = this.router.url;
    if (!currentUrl || currentUrl === '/' || currentUrl === '') {
      await firstValueFrom(
        this.router.events.pipe(filter((event: any) => event?.constructor?.name === 'NavigationEnd'))
      );
    }

    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
    await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
  }
  private async setupStatusBar() {
    try {
      await StatusBar.setStyle({ style: Style.Dark }); // dark icons on light UI
    } catch (e) {
      console.warn('StatusBar setup failed', e);
    }
  }

  private async setupKeyboardBehavior() {
    try {
      // Ensure only the body resizes; headers/toolbars remain fixed
      await Keyboard.setResizeMode({ mode: KeyboardResize.Body });
      // Disable native scroll assistance to avoid content jumps
      await Keyboard.setScroll({ isDisabled: true });

      // Add body class toggles so pages can react in CSS
      Keyboard.addListener('keyboardWillShow', (e: KeyboardInfo) => {
        document.body.classList.add('keyboard-open');
      });
      Keyboard.addListener('keyboardDidShow', (e: KeyboardInfo) => {
        document.body.classList.add('keyboard-open');
      });
      Keyboard.addListener('keyboardWillHide', () => {
        document.body.classList.remove('keyboard-open');
      });
      Keyboard.addListener('keyboardDidHide', () => {
        document.body.classList.remove('keyboard-open');
      });
    } catch (e) {
      console.warn('Keyboard setup failed', e);
    }
  }

  private shouldSetupOneSignal(): boolean {
    // Always initialize OneSignal on native runtime so foreground suppression/clear handlers attach.
    return this.isNativeRuntime();
  }

  private shouldCheckVersion(): boolean {
    return !!(this.id && this.id.length > 0) && this.isNativeRuntime();
  }

  private isNativeRuntime(): boolean {
    return Capacitor.isNativePlatform();
  }

  private async checkStorageData() {
    try {
      const localnotify = await this.storage.get('notify');
      if (localnotify && localnotify !== '') {
        this.global.notify = localnotify;
      }

      if (!this.global.id) {
        this.chatuser = await this.storage.get('chatuser');
        
        if (this.chatuser) {
          this.id = this.chatuser.id;
          this.password = this.chatuser.password;
          this.global.name = this.chatuser.name;
          this.global.password = this.chatuser.password;
        }
      } else {
        this.id = this.global.id;
        this.password = this.global.password;
        this.chatuser = this.global.chatuser as ChatUser;
      }
    } catch (error) {
      console.error('Error checking storage data:', error);
    }
  }

  private async setupOneSignal() {
    try {
      await this.loadUserInfo();
      
      if (!this.platform.is('desktop')) {
        await this.initializeOneSignal();
        this.setupForegroundNotificationGuard();
        await this.handleOneSignalSetup();
      }
    } catch (error) {
      console.error('Error setting up OneSignal:', error);
    }
  }

  private async loadUserInfo() {
    if (!this.id || this.id === '') {
      const userinfo: UserInfo = await this.storage.get('userinfo') || {
        id: '', password: '', deviceID: '', devtoken: '', pushdev: false
      };

      this.setUserInfoFromStorage(userinfo);
    }
  }

  private setUserInfoFromStorage(userinfo: UserInfo) {
    this.global.userinfo = userinfo;
    this.global.id = userinfo.id;
    this.global.password = userinfo.password;
    this.global.pushid = userinfo.deviceID;
    this.global.pushtoken = userinfo.devtoken;
    this.global.pushdev = userinfo.pushdev;
    
    this.id = userinfo.id;
    this.password = userinfo.password;
    this.pushid = userinfo.deviceID;
    this.pushtoken = userinfo.devtoken;
    this.pushdev = userinfo.pushdev;
  }

  private async initializeOneSignal() {
    const iosSettings = { 
      kOSSettingsKeyAutoPrompt: false, 
      kOSSettingsKeyInAppLaunchURL: false 
    };

    window.plugins.OneSignal.Debug.setLogLevel(6);
    window.plugins.OneSignal.initialize(this.ONESIGNAL_APP_ID);

    this.uid = await window.plugins.OneSignal.User.pushSubscription.getIdAsync();   
    this.utoken = await window.plugins.OneSignal.User.pushSubscription.getTokenAsync();  
    
    this.global.pushid = this.uid;
    this.global.pushtoken = this.utoken;
  }

  private async handleOneSignalSetup() {
    // Request permission for notifications
    await window.plugins.OneSignal.Notifications.requestPermission(true);
    
    if (typeof window.plugins.OneSignal.promptForPushNotificationsWithUserResponse === 'function') {
      window.plugins.OneSignal.promptForPushNotificationsWithUserResponse((accepted: boolean) => {
        console.log("User accepted notifications: " + accepted);
      });
    }

    this.clearAllNotifications();
    this.setupForegroundNotificationGuard();
    await this.getOneSignalPlayerId();
    await this.loginOneSignalExternalId();

    if (this.pushid && this.pushid.length > 0) {
      await this.updateBadge();
    }
    
    if (this.id && this.pushid && !this.pushdev) {
      await this.updateDeviceInfo();
    }
  }

  private async getOneSignalPlayerId() {
    if (!this.uid) return;

    const userinfo: UserInfo = {
      id: this.id,
      password: this.password,
      deviceID: this.uid,
      devtoken: this.utoken,
      pushdev: false
    };

    await this.storage.set('userinfo', userinfo);
    this.setGlobalPushInfo();

    if (this.uid.length > 0) {
      await this.updateDeviceIdOnServer();
    }
  }

  private setGlobalPushInfo() {
    this.global.pushid = this.uid;
    this.global.pushtoken = this.utoken;
    this.pushid = this.uid;
    this.pushtoken = this.utoken;
  }

  private async updateDeviceIdOnServer() {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    const options = { 
      id: this.id, 
      deviceID: this.uid, 
      devtoken: this.utoken,
      platform: platform
    };
    const url = `${this.API_BASE_URL}update_deviceid.php`;

    try {
      const response: any = await lastValueFrom(
        this.http.post(url, JSON.stringify(options), { headers })
      );

      if (response === 'update success' || response === 'deviceID existed') {
        await this.updateUserInfoWithDeviceSuccess();
      }
    } catch (error) {
      console.error('Error updating device ID on server:', error);
    }
  }

  private async updateUserInfoWithDeviceSuccess() {
    const userinfo: UserInfo = {
      id: this.id,
      password: this.password,
      deviceID: this.uid,
      devtoken: this.utoken,
      pushdev: true
    };

    await this.storage.set('userinfo', userinfo);
    this.global.pushdev = true;
  }

  private async updateDeviceInfo() {
    if (!this.pushid || this.pushid.length === 0) return;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    const options = {
      id: this.id,
      deviceID: this.pushid,
      devtoken: this.pushtoken,
      platform: platform
    };
    const url = `${this.API_BASE_URL}update_deviceid.php`;

    try {
      const data: any = await lastValueFrom(
        this.http.post(url, JSON.stringify(options), { headers })
      );
      
      const result = JSON.parse(data.data);

      if (result === 'update success' || result === 'deviceID existed') {
        await this.updateUserInfoWithDeviceSuccess();
      }
    } catch (error) {
      console.error('Error updating device info:', error);
    }
  }

  private async setupOneSignalWeb() {
    try {
      await this.loadUserInfo();
      const oneSignal = await this.initializeOneSignalWeb();
      await this.loginOneSignalWebExternalId(oneSignal, true);
      this.scheduleOneSignalWebExternalIdRetry(oneSignal);
    } catch (error) {
      console.error('Error setting up OneSignal Web SDK:', error);
    }
  }

  private async initializeOneSignalWeb(): Promise<OneSignalWebSdk> {
    if (this.oneSignalWebInitPromise) {
      return this.oneSignalWebInitPromise;
    }

    this.oneSignalWebInitPromise = new Promise<OneSignalWebSdk>((resolve, reject) => {
      const start = () => {
        window.OneSignalDeferred = window.OneSignalDeferred || [];
        window.OneSignalDeferred.push(async (oneSignal: OneSignalWebSdk) => {
          try {
            const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname);
            await oneSignal.init({
              appId: this.ONESIGNAL_APP_ID,
              serviceWorkerPath: 'onesignal/OneSignalSDKWorker.js',
              serviceWorkerParam: { scope: '/onesignal/' },
              allowLocalhostAsSecureOrigin: isLocalhost
            });
            resolve(oneSignal);
          } catch (error) {
            if (String((error as any)?.message || error).includes('already initialized')) {
              resolve(oneSignal);
              return;
            }
            reject(error);
          }
        });
      };

      if (window.OneSignal) {
        start();
        return;
      }

      const existingScript = document.querySelector('script[data-onesignal-web-sdk="true"]');
      if (existingScript) {
        start();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
      script.defer = true;
      script.dataset['onesignalWebSdk'] = 'true';
      script.onload = start;
      script.onerror = () => reject(new Error('Failed to load OneSignal Web SDK'));
      document.head.appendChild(script);
    });

    return this.oneSignalWebInitPromise;
  }

  private async loginOneSignalExternalId() {
    if (!this.id || !(window as any)?.plugins?.OneSignal?.login) {
      return;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = `${this.CHAT_API_BASE_URL}onesignal_external_id.php`;

    try {
      const response: any = await lastValueFrom(
        this.http.post(url, JSON.stringify({ id: this.id }), { headers })
      );

      if (response?.success && response?.enabled && response?.onesignal_external_id) {
        const externalId = String(response.onesignal_external_id);
        window.plugins.OneSignal.login(externalId);
        await this.syncNativeOneSignalSubscription(this.id, externalId);
      }
    } catch (error) {
      console.error('Error logging into OneSignal External ID:', error);
    }
  }

  private async loginOneSignalWebExternalId(oneSignal: OneSignalWebSdk, promptForPush: boolean) {
    const userId = await this.currentWebOneSignalUserId();
    if (!userId) {
      return;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = `${this.CHAT_API_BASE_URL}onesignal_external_id.php`;

    try {
      const response: any = await lastValueFrom(
        this.http.post(url, JSON.stringify({ id: userId }), { headers })
      );

      if (response?.success && response?.enabled && response?.onesignal_external_id) {
        const externalId = String(response.onesignal_external_id);
        await oneSignal.login(externalId);

        if (
          promptForPush &&
          oneSignal.Notifications.isPushSupported() &&
          !oneSignal.Notifications.permission
        ) {
          if (oneSignal.Slidedown?.promptPush) {
            await oneSignal.Slidedown.promptPush();
          } else {
            await oneSignal.Notifications.requestPermission();
          }
        }

        await this.syncOneSignalSubscriptionToServer(
          userId,
          externalId,
          oneSignal.User?.PushSubscription?.id || '',
          this.currentBrowserOneSignalPlatform(),
          !!oneSignal.User?.PushSubscription?.optedIn
        );
      }
    } catch (error) {
      console.error('Error logging into OneSignal Web External ID:', error);
    }
  }

  private scheduleOneSignalWebExternalIdRetry(oneSignal: OneSignalWebSdk) {
    window.setTimeout(() => {
      this.loginOneSignalWebExternalId(oneSignal, false);
    }, 2000);

    window.setTimeout(() => {
      this.loginOneSignalWebExternalId(oneSignal, false);
    }, 8000);
  }

  private async currentWebOneSignalUserId(): Promise<string> {
    const directId = String(this.id || this.global.id || '').trim();
    if (directId) {
      return directId;
    }

    const chatuser = await this.storage.get('chatuser');
    if (chatuser?.id) {
      return String(chatuser.id).trim();
    }

    const userinfo = await this.storage.get('userinfo');
    if (userinfo?.id) {
      return String(userinfo.id).trim();
    }

    return '';
  }

  private async syncNativeOneSignalSubscription(userId: string, externalId: string) {
    const pushSubscription = (window as any)?.plugins?.OneSignal?.User?.pushSubscription;
    if (!userId || !externalId || !pushSubscription?.getIdAsync) {
      return;
    }

    try {
      const subscriptionId = await pushSubscription.getIdAsync();
      if (!subscriptionId) {
        return;
      }

      let optedIn = true;
      try {
        if (pushSubscription.getOptedInAsync) {
          optedIn = !!(await pushSubscription.getOptedInAsync());
        } else if (typeof pushSubscription.optedIn !== 'undefined') {
          optedIn = !!pushSubscription.optedIn;
        }
      } catch (error) {
      }

      await this.syncOneSignalSubscriptionToServer(
        userId,
        externalId,
        String(subscriptionId),
        Capacitor.getPlatform() === 'ios' ? 'ios' : 'android',
        optedIn
      );
    } catch (error) {
      console.error('Error syncing native OneSignal subscription:', error);
    }
  }

  private async syncOneSignalSubscriptionToServer(
    userId: string,
    externalId: string,
    subscriptionId: string,
    platform: string,
    optedIn: boolean
  ) {
    if (!userId || !externalId || !subscriptionId) {
      return;
    }

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const url = `${this.CHAT_API_BASE_URL}onesignal_subscription_sync.php`;
    const clientInstallId = await this.currentOneSignalClientInstallId();
    const payload = {
      id: userId,
      onesignal_external_id: externalId,
      subscription_id: subscriptionId,
      client_install_id: clientInstallId,
      platform,
      opted_in: optedIn
    };

    try {
      await lastValueFrom(
        this.http.post(url, JSON.stringify(payload), { headers })
      );
    } catch (error) {
      console.error('Error syncing OneSignal subscription:', error);
    }
  }

  private async currentOneSignalClientInstallId(): Promise<string> {
    const storageKey = 'onesignal_client_install_id';
    const cachedValue = String((window as any).__oneSignalClientInstallId || '').trim();
    if (cachedValue) {
      return cachedValue;
    }

    try {
      const localValue = window.localStorage?.getItem(storageKey);
      if (localValue) {
        (window as any).__oneSignalClientInstallId = localValue;
        return localValue;
      }
    } catch (error) {}

    const storedValue = await this.storage.get(storageKey);
    const latestCachedValue = String((window as any).__oneSignalClientInstallId || '').trim();
    if (latestCachedValue) {
      return latestCachedValue;
    }

    try {
      const latestLocalValue = window.localStorage?.getItem(storageKey);
      if (latestLocalValue) {
        (window as any).__oneSignalClientInstallId = latestLocalValue;
        return latestLocalValue;
      }
    } catch (error) {}

    if (storedValue) {
      const value = String(storedValue);
      (window as any).__oneSignalClientInstallId = value;
      try {
        window.localStorage?.setItem(storageKey, value);
      } catch (error) {}
      return value;
    }

    const randomId = this.generateOneSignalClientInstallId();
    (window as any).__oneSignalClientInstallId = randomId;
    try {
      window.localStorage?.setItem(storageKey, randomId);
    } catch (error) {}
    await this.storage.set(storageKey, randomId);

    return randomId;
  }

  private generateOneSignalClientInstallId(): string {
    const cryptoApi = window.crypto as Crypto | undefined;
    if (cryptoApi?.randomUUID) {
      return cryptoApi.randomUUID();
    }

    const randomText = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    return `install_${Date.now().toString(36)}_${randomText}`.slice(0, 128);
  }

  private currentBrowserOneSignalPlatform(): string {
    const userAgent = window.navigator.userAgent.toLowerCase();
    if (userAgent.includes('edg/')) {
      return 'edge';
    }
    if (userAgent.includes('chrome/') || userAgent.includes('crios/')) {
      return 'chrome';
    }
    if (userAgent.includes('safari/')) {
      return 'safari';
    }
    return 'web';
  }

  private async updateBadge(): Promise<any> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { id: this.id };
    const url = `${this.API_BASE_URL}check_notification.php`;
    
    return new Promise((resolve, reject) => {
      this.http.post(url, JSON.stringify(options), { headers })
        .subscribe({
          next: (data: any) => {
            if (!this.global.notification) {
              const result = JSON.parse(data.data);
              if (result > 0) {
                this.global.notification = true;
              }
            }
            resolve(data);
          },
          error: (error) => {
            console.error('Error updating badge:', error);
            reject(error);
          }
        });
    });
  }

  private async checkVersion() {
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';

    try {
      const info = await App.getInfo();
      this.app_version = info?.version || '';
      if (this.app_version) {
        this.sendVersionToServer(platform);
      }
    } catch (err) {
      console.error('Error getting app version:', err);
    }
  }

  private parseVersionCheckResult(data: any): string {
    try {
      if (typeof data === 'string') {
        return data;
      }

      if (typeof data?.data === 'string') {
        const parsed = JSON.parse(data.data);
        return typeof parsed === 'string' ? parsed : '';
      }

      if (typeof data?.result === 'string') {
        return data.result;
      }
    } catch (error) {
      console.warn('Version response parse warning:', error);
    }

    return '';
  }
  private setupForegroundNotificationGuard() {
    if (this.foregroundGuardAttached) {
      return;
    }
    try {
      const oneSignal = (window as any)?.plugins?.OneSignal;
      const notifications = oneSignal?.Notifications;
      if (!notifications?.addEventListener) {
        return;
      }

      notifications.addEventListener('foregroundWillDisplay', (event: any) => {
        try {
          if (event?.preventDefault) {
            // Block notification UI when app is already foreground.
            event.preventDefault();
            event.preventDefault(true);
          }
        } catch {}

        // Ensure icon badge and delivered notifications are cleared immediately in foreground.
        setTimeout(() => this.clearAllNotifications(), 0);
      });
      this.foregroundGuardAttached = true;
    } catch (error) {
      console.warn('Failed to set foreground notification guard:', error);
    }
  }

  private sendVersionToServer(platform: string) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = {
      id: this.id,
      deviceID: this.pushid || this.global.pushid || '',
      version: this.app_version,
      platform: platform
    };
    const url = `${this.API_BASE_URL}version.php`;

    this.http.post(url, JSON.stringify(options), { headers })
      .subscribe({
        next: (data: any) => {
          const result = this.parseVersionCheckResult(data);
          if (result === 'request app update') {
            this.presentErrorAlert('您的 APP 版本已過舊，請前往商店更新。');
          }
        },
        error: (err) => {
          console.error('Error checking version:', err);
        }
      });
  }
  private async verifyAndNavigate() {
    if (this.chatuser?.role === 'user') {
      await this.joinChatting();
    } else if (this.chatuser?.role === 'admin') {
      const browserTargetChatId = this.getBrowserTargetChatId();
      if (browserTargetChatId) {
        await this.joinAdminTargetChat(browserTargetChatId);
        return;
      }

      await this.joinChatlist();
    }
  }

  private async joinChatting() {
    if (!this.chatuser) return;

    this.connectSocketIfNeeded();
    this.socket.emit('set-nickname', { nickname: this.chatuser.id, role: 'user' });
    this.socket.emit('move_to_room', this.chatuser.name);
    
    this.setGlobalChatInfo('user');
    void this.preloadEmojiCache();
    
    await
    await this.router.navigate(['chatting'], { replaceUrl: true });
    
    this.createProfileIcon();
  }

  private async joinChatlist() {
    if (!this.chatuser) return;

    this.connectSocketIfNeeded();
    this.socket.emit('set-nickname', { nickname: this.chatuser.id, role: 'admin' });
    this.socket.emit('move_to_room', this.chatuser.name);
    
    this.setGlobalChatInfo('admin');
    void this.preloadEmojiCache();

    const browserTargetChatId = this.getBrowserTargetChatId();
    if (browserTargetChatId) {
      await this.router.navigate(['chatting'], {
        replaceUrl: true,
        queryParams: { chatto: browserTargetChatId }
      });
      return;
    }
    
    await
    await this.router.navigate(['chat-list'], { replaceUrl: true });
  }

  private async joinAdminTargetChat(targetChatId: string) {
    if (!this.chatuser) return;

    this.connectSocketIfNeeded();
    this.socket.emit('set-nickname', { nickname: this.chatuser.id, role: 'admin' });
    this.setGlobalChatInfo('admin');
    void this.preloadEmojiCache();

    await this.router.navigate(['chatting'], {
      replaceUrl: true,
      queryParams: { chatto: targetChatId }
    });
  }

  /**
   * Warm the WebView/browser image cache after login without delaying navigation.
   * The server-provided version query keeps immutable cached files fresh when an
   * emoji is replaced.
   */
  private async preloadEmojiCache(): Promise<void> {
    if (this.emojiPreloadStarted || !this.chatuser || typeof Image === 'undefined') {
      return;
    }

    this.emojiPreloadStarted = true;
    const credentials = this.chatuser.role === 'admin'
      ? { user_id: this.chatuser.id, password: this.chatuser.password }
      : {};
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const emojiRoot = 'https://chat.ivydental.com.tw/imgs/emoji/';

    try {
      const categories: any = await firstValueFrom(
        this.http.post(
          `${this.CHAT_API_BASE_URL}emoji_path.php`,
          JSON.stringify(credentials),
          { headers }
        )
      );

      if (!Array.isArray(categories)) {
        return;
      }

      const urls: string[] = [];
      for (const category of categories) {
        if (category?.file) {
          urls.push(
            `${emojiRoot}${String(category.file).replace(/^\/+/, '')}?v=${category.version || this.emojiFallbackVersion}`
          );
        }

        const icons: any = await firstValueFrom(
          this.http.post(
            `${this.CHAT_API_BASE_URL}emoji.php`,
            JSON.stringify({ ...credentials, path: category.path }),
            { headers }
          )
        );

        if (!Array.isArray(icons)) {
          continue;
        }

        for (const rawIcon of icons) {
          const icon = typeof rawIcon === 'string'
            ? { file: rawIcon, version: this.emojiFallbackVersion }
            : rawIcon;
          const relativeFile = String(icon?.file || '').replace(/^\/+/, '');
          if (!/\.(png|jpe?g|gif|webp)$/i.test(relativeFile) || relativeFile === 'preview_contact_sheet.png') {
            continue;
          }
          const imagePath = relativeFile.includes('/')
            ? relativeFile
            : `${category.path}/${relativeFile}`;
          urls.push(`${emojiRoot}${imagePath}?v=${icon.version || this.emojiFallbackVersion}`);
        }
      }

      const uniqueUrls = Array.from(new Set(urls));
      let nextIndex = 0;
      const worker = async () => {
        while (nextIndex < uniqueUrls.length) {
          const imageUrl = uniqueUrls[nextIndex++];
          await new Promise<void>((resolve) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => resolve();
            image.src = imageUrl;
          });
        }
      };

      await Promise.all(Array.from({ length: Math.min(4, uniqueUrls.length) }, () => worker()));
    } catch (error) {
      // Preloading is an optimization; normal on-demand loading remains available.
      console.warn('[Emoji] Background preload skipped:', error);
      this.emojiPreloadStarted = false;
    }
  }

  private getBrowserTargetChatId(): string {
    if (this.isNativeRuntime()) {
      return '';
    }

    try {
      const params = new URLSearchParams(window.location.search || '');
      const targetId = String(params.get('chatto') || '').trim();
      const isOpenChatRequest = params.get('openChat') === '1';
      const isDirectChatRoute = window.location.pathname.replace(/\/+$/, '') === '/chatting';

      if (!targetId || (!isOpenChatRequest && !isDirectChatRoute)) {
        return '';
      }

      return targetId;
    } catch (error) {
      console.warn('[INFO] Unable to parse browser target chat id', error);
      return '';
    }
  }

  private connectSocketIfNeeded() {
    const ioSocket = (this.socket as any)?.ioSocket;
    if (this.socket?.connected || ioSocket?.connected || ioSocket?.connecting) {
      return;
    }
    this.socket.connect();
  }

  private setGlobalChatInfo(role: 'user' | 'admin') {
    if (!this.chatuser) return;

    this.global.chatid = this.chatuser.id;
    this.global.name = this.chatuser.name;
    
    if (role === 'user') {
      this.global.roomTitle = "常春藤牙醫診所";
      this.global.socketroom = this.chatuser.name;
      this.global.role = "user";
      this.global.chatto = "admin";
    } else {
      this.global.roomTitle = "";
      this.global.socketroom = "";
      this.global.role = "admin";
      this.global.chatto = "";
    }
  }

  private createProfileIcon() {
    if (!this.chatuser) return;

    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const options = { 
      id: this.chatuser.id, 
      name: this.chatuser.name 
    };
    const url = `${this.CHAT_API_BASE_URL}createprofileicon.php`;
    
    this.http.post(url, JSON.stringify(options), { headers })
      .subscribe({
        next: (_iconcreated: any) => {},
        error: (error: any) => {
          console.log('建立頭像失敗', error);
        }
      });
  }

  private async presentErrorAlert(msg: string) {
    const alert = await this.alertCtrl.create({
      header: 'APP 更新通知',
      message: msg,
      buttons: ['確定']
    });
    await alert.present();
  }

  private async showNotification(title: string, msg: string) {
    const alert = await this.alertCtrl.create({
      header: title,
      message: msg,
      buttons: [{
        text: '確定'
      }]
    });
    await alert.present();
  }

  private clearAllNotifications() {
    try {
      const oneSignal = (window as any)?.plugins?.OneSignal;

      // OneSignal Cordova v5 API
      if (oneSignal?.Notifications?.clearAll) {
        oneSignal.Notifications.clearAll();
      }

      // Extra compatibility path for some Android launcher badge behaviors
      const cordovaExec = (window as any)?.cordova?.exec;
      if (typeof cordovaExec === 'function') {
        cordovaExec(
          () => {},
          () => {},
          'OneSignalPush',
          'clearAllNotifications',
          []
        );
      }

      // Reset in-app badge flag used by chat-list indicator
      this.global.notification = false;
    } catch (error) {
      console.warn('Failed to clear notifications:', error);
    }
  }

  private handleSharedData(data: any) {
    console.log('[DEBUG]  AppComponent: Received shared data:', data);
    console.log('[DEBUG]  AppComponent: Data properties - type:', data?.type, 'filename:', data?.filename, 'content length:', data?.content?.length, 'mimeType:', data?.mimeType);

    // Create a unique key to prevent duplicate processing
    const dataKey = data && data.type ? `${data.type}_${data.filename || data.content}_${Date.now()}` : `text_${data}_${Date.now()}`;

    // Store the shared file
    let sharedFile;
    if (data && typeof data === 'string') {
      // Handle text sharing (legacy string format)
      console.log('[DEBUG]  AppComponent: Handling text sharing (string format)');
      sharedFile = this.sharedFileService.addSharedFile({
        type: 'text',
        filename: 'shared_text.txt',
        content: data
      });
      // Don't show notification for text sharing - will be handled in chat
    } else if (data && data.type === 'text') {
      // Handle text sharing (object format from Android intent)
      console.log('[DEBUG]  AppComponent: Handling text sharing (object format)');
      sharedFile = this.sharedFileService.addSharedFile({
        type: 'text',
        filename: 'shared_text.txt',
        content: data.content
      });
      // Don't show notification for text sharing - will be handled in chat
    } else if (data && data.type) {
      // Check if this is actually text content disguised as a file (common with URLs)
      const content = data.content || data.uri || '';
      if (this.isTextContent(content, data.mimeType)) {
        console.log('[DEBUG]  AppComponent: Detected text content disguised as file - treating as text');
        sharedFile = this.sharedFileService.addSharedFile({
          type: 'text',
          filename: 'shared_text.txt',
          content: content
        });
      } else {
        // Handle file sharing
        console.log('[DEBUG]  AppComponent: Handling file sharing - type:', data.type, 'filename:', data.filename, 'content:', data.content);
        // Ensure filename is included in the shared file data
        const fileData = {
          ...data,
          filename: data.filename || data.name || 'unknown_file'
        };
        sharedFile = this.sharedFileService.addSharedFile(fileData);
        const filename = data.filename || data.name || '未知檔案';
        // Don't show notification for file sharing - will be handled in chat
      }
    } else {
      console.log('[DEBUG]  AppComponent: Unknown shared data format:', data);
      return;
    }

    // Send shared file as a message to the chatting page
    if (sharedFile) {
      console.log('[DEBUG]  AppComponent: Sending shared file to chatting page:', sharedFile);
      this.sendSharedFileAsMessage(sharedFile);
    }
  }

  // Helper method to detect if content is actually text (URLs, plain text, etc.)
  private isTextContent(content: string, mimeType?: string): boolean {
    if (!content) return false;
    
    // Check MIME type first
    if (mimeType && (mimeType.startsWith('text/') || mimeType === 'application/json')) {
      return true;
    }
    
    // Check if content looks like a URL
    const urlPattern = /^https?:\/\/.+/i;
    if (urlPattern.test(content)) {
      return true;
    }
    
    // Check if content is plain text (not binary data)
    try {
      // If it's a data URL, check the MIME type
      if (content.startsWith('data:')) {
        const mimeMatch = content.match(/^data:([^;]+)/);
        if (mimeMatch && mimeMatch[1].startsWith('text/')) {
          return true;
        }
      }
      
      // Check if content is valid text (not binary)
      const textContent = decodeURIComponent(content);
      if (textContent.length < 1000 && /^[\x20-\x7E\s]*$/.test(textContent)) {
        return true;
      }
    } catch (e) {
      // If decoding fails, it's likely binary content
      return false;
    }
    
    return false;
  }

  private sendSharedFileAsMessage(sharedFile: any) {
    // Dispatch a custom event that the chatting page can listen to
    const sharedFileMessage = {
      type: 'shared_file',
      data: sharedFile,
      timestamp: Date.now()
    };
    
    console.log('[DEBUG]  AppComponent: Dispatching sharedFileMessage event:', sharedFileMessage);
    window.dispatchEvent(new CustomEvent('sharedFileMessage', { 
      detail: sharedFileMessage
    }));
  }

  // [INFO] Global ARIA-hidden observer and fix
  private setupGlobalAriaHiddenObserver() {
    // Set up a global observer for all loading elements
    const globalObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
          const target = mutation.target as HTMLElement;
          if (target.tagName === 'ION-LOADING' && target.getAttribute('aria-hidden') === 'true' && target.offsetParent !== null) {
            target.removeAttribute('aria-hidden');
            console.log('[DEBUG]  Global observer: Removed aria-hidden from loading element');
          }
        }
      });
    });

    // Start observing the document for loading elements
    globalObserver.observe(document.body, { 
      childList: true, 
      subtree: true, 
      attributes: true, 
      attributeFilter: ['aria-hidden'] 
    });

    // Set up a more frequent periodic check as backup
    const globalInterval = setInterval(() => {
      const allLoadingElements = document.querySelectorAll('ion-loading');
      allLoadingElements.forEach((element: HTMLElement) => {
        if (element.offsetParent !== null && element.getAttribute('aria-hidden') === 'true') {
          element.removeAttribute('aria-hidden');
          console.log('[DEBUG]  Global periodic fix: Removed aria-hidden from loading element');
        }
      });
    }, 50); // Check every 50ms for faster response

    // Store references for cleanup if needed
    (window as any).globalAriaHiddenFix = {
      observer: globalObserver,
      interval: globalInterval
    };

    console.log('[INFO] Global ARIA-hidden fix setup complete');
  }
}







